import omit from "lodash-es/omit";
import { type FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { toastSuccessMessage } from "../../utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "../../utils/amplitude";

import { LabeledTextInput } from "./LabeledTextInput";
import { LabeledTextarea } from "./LabeledTextarea";
import { SubmitOrCancelButtons } from "./SubmitOrCancelButtons";

import { LMSConnectionIndicator } from "components/materials/section/LMSConnectionIndicator";
import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { showDisconnectConfirmationModal } from "components/modals/DisconnectSectionModal";
import { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectDesignatedSection, selectLmsType } from "store/selectors";
import { isLinkedSection } from "utils/publishing";
import { useSectionSettingActions } from "utils/section";

type SectionFormValues = {
  title: string;
  description: string;
  external_section_id: string;
  course_id: string;
  term_id: string;
};

type SectionSettingsSectionInfoFormProps = {
  className?: string;
  onClose: () => void;
};

export const SectionSettingsSectionInfoForm: FC<SectionSettingsSectionInfoFormProps> = ({
  className,
  onClose,
}) => {
  const currentSection = useAppSelector(selectDesignatedSection);
  const currentLmsType = useAppSelector(selectLmsType);
  const { canAuthorCourse } = useCapabilities();

  const form = useForm<SectionFormValues>({
    defaultValues: {
      title: currentSection?.title,
      description: currentSection?.description || "",
      external_section_id: currentSection?.external_section_id || "",
      course_id: currentSection?.course_id || "",
      term_id: currentSection?.term_id || "",
    },
  });

  const courseId = form.watch("course_id");
  const termId = form.watch("term_id");

  const { handleSectionDelete } = useSectionSettingActions();

  if (!currentSection) {
    return null;
  }

  const onHandleSectionDisconnect = async () => {
    if (!currentSection.lms_section_id || !currentLmsType) return;
    onClose();

    await showDisconnectConfirmationModal({
      sectionId: currentSection.id,
      lmsType: currentLmsType,
    });
  };

  const onHandleSectionDelete = () => {
    onClose();

    handleSectionDelete(currentSection);
  };

  const isLinked = isLinkedSection(currentSection);

  const onSubmit: SubmitHandler<SectionFormValues> = (formData) => {
    if (isLinked) {
      return;
    }

    trackEvent("Section Settings - Save changes", BUTTON_EVENT_CATEGORY);
    const dataWithoutEmptyStrings = {
      ...formData,
      description: formData.description || null,
      external_section_id: formData.external_section_id || null,
    };

    const fieldsToOmit: (keyof SectionFormValues)[] = canAuthorCourse
      ? []
      : ["external_section_id"];
    const data = omit(dataWithoutEmptyStrings, fieldsToOmit) as Partial<SectionType>;

    return storeApi.sections.partial_update({ id: currentSection.id, ...data }).then(() => {
      toastSuccessMessage(t("success.toasts.updated_section_settings"));
      onClose();
    });
  };

  return (
    <FormProvider {...form}>
      <form
        className={twMerge(
          "flex flex-col text-sm font-medium leading-[1.3125rem] text-black-tint-10",
          className,
        )}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-2">
          {!isLinked && (
            <label className="flex items-center gap-4">
              <div>{t("home.courses.select_course_label")}</div>

              <input type="hidden" {...form.register("course_id")} />

              <div className="inline-block">
                <CourseMenu
                  menuClassName="max-h-344"
                  menuType="menu"
                  selectedCourseId={courseId}
                  showEmptyOption={false}
                  adminAccessibleOnly
                  onCourseChange={(id) => form.setValue("course_id", id)}
                />
              </div>
            </label>
          )}

          <LabeledTextInput
            {...form.register("title", { required: t("error.field_required"), disabled: isLinked })}
            label={t("glossary.section_title")}
            required
          />

          {!isLinked && (
            <label className="flex items-center gap-4">
              <div>{t("home.terms.select_term_label")}</div>

              <input type="hidden" {...form.register("term_id")} />

              <div className="inline-block">
                <TermMenu
                  menuClassName="max-h-344"
                  menuType="menu"
                  selectedTermId={termId}
                  showEmptyOption={false}
                  onTermChange={(id) => form.setValue("term_id", id)}
                />
              </div>
            </label>
          )}

          <LabeledTextarea
            {...form.register("description", { disabled: isLinked })}
            label={t("glossary.section_description")}
            minRows={3}
            placeholder={t("home.placeholder.section_description")}
          />

          <LabeledTextInput
            wrapperClassName={
              !canAuthorCourse && !currentSection.external_section_id ? "hidden" : undefined
            }
            {...form.register("external_section_id", { disabled: !canAuthorCourse || isLinked })}
            label={t("settings.external_section_id")}
            postLabel={<Tooltip text={t("settings.editable_admins_only")} />}
            required
          />

          <div className="flex flex-col gap-1">
            <h3 className="mt-1 text-xs font-normal leading-[1.25rem] text-black-tint-20">
              {t("settings.lms_integration")}
            </h3>

            <div className="flex flex-row items-center justify-between">
              <LMSConnectionIndicator />

              {currentSection.lms_section_id && (
                <SquareButton
                  disabled={isLinked}
                  kind="caution"
                  size="xs"
                  onClick={onHandleSectionDisconnect}
                >
                  {t("settings.disconnect_section")}
                </SquareButton>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-1">
          <Tooltip text={!canAuthorCourse ? t("settings.delete_section_assistants_only") : ""}>
            <SquareButton
              disabled={!canAuthorCourse || isLinked}
              kind="caution"
              size="m"
              onClick={onHandleSectionDelete}
            >
              {t("settings.delete_section")}
            </SquareButton>
          </Tooltip>

          <SubmitOrCancelButtons disableSubmit={isLinked} onCancel={onClose} />
        </div>
      </form>
    </FormProvider>
  );
};
